<script setup lang="ts">
// The overlay one enlarged card is shown in. It is mounted once by the analysis
// pane and stays mounted: the card that is enlarged moves into this layer's
// persistent host with a `Teleport`, so the card is the same component instance
// — its chart keeps the resize observer that redraws it at the new box, and its
// handlers keep the closures they had in the pane.
//
// Two deliberate splits:
//
// * Only the backdrop takes clicks. The shell's content (the enlarged card,
//   including a bar or a point a panel drills through) must behave exactly as
//   it does in the pane, so nothing inside the shell suppresses or intercepts
//   pointer events; the backdrop sits behind the shell and is the single
//   "click outside to close" target.
// * The host element is inside the shell rather than teleported separately, so
//   the card scrolls with the shell and the sticky bar's X stays reachable on a
//   tall card.
import { onMounted, onUnmounted } from "vue";

import {
	ANALYSIS_CARD_HOST_ID,
	cardHostGeneration,
	cardHostMounted,
	collapseCard,
	expandedCardId,
} from "./cardExpander";

/** Escape closes the overlay, as it does for the pane's other dismissable
 *  layers. It is a no-op while nothing is expanded, and while a panel's
 *  explainer is open on top of this layer: Escape there belongs to the
 *  topmost surface, and closing the card behind the modal would strand it. */
function onKeydown(event: KeyboardEvent): void {
	if (event.key !== "Escape") return;
	if (document.querySelector(".modal.show")) return;
	collapseCard();
}

onMounted(() => {
	window.addEventListener("keydown", onKeydown);
	// Cards gate their expander on this: a card rendered where this layer is
	// not mounted would teleport into an absent host and disappear.
	cardHostMounted.value = true;
	// A new host element exists now. Cards that mounted against an earlier layer
	// still hold that layer's host; bumping the generation makes them rebuild
	// their Teleport and resolve this one instead.
	cardHostGeneration.value += 1;
});

onUnmounted(() => {
	window.removeEventListener("keydown", onKeydown);
	cardHostMounted.value = false;
	// A card still teleported into the host would be dropped together with the
	// layer, leaving `expandedCardId` pointing at a card nobody can see; clear
	// it so the next mount starts closed.
	collapseCard();
});
</script>

<template>
	<div v-show="expandedCardId !== null" class="card-layer">
		<!-- The only click-to-close surface. Behind the shell, so a click on the
		     enlarged card never reaches it. -->
		<div class="card-layer-backdrop" @click="collapseCard"></div>

		<div class="card-layer-shell">
			<!-- The card states its own title, so the bar carries nothing but the
			     close control. Sticky, because a tall card scrolls inside the
			     shell and the X must stay reachable at every scroll position. -->
			<div class="card-layer-bar">
				<button
					type="button"
					class="card-layer-close"
					aria-label="Close the enlarged card"
					title="Close the enlarged card"
					@click="collapseCard"
				>
					<i class="bi bi-x-lg"></i>
				</button>
			</div>

			<!-- The persistent teleport target. Always rendered, even while the
			     layer is hidden, so a card's `Teleport` always has somewhere to
			     resolve. -->
			<div :id="ANALYSIS_CARD_HOST_ID" class="card-host"></div>
		</div>
	</div>
</template>

<style scoped>
.card-layer {
	position: fixed;
	inset: 0;
	/* Above the workbench and its floating panels, including a bootstrap modal
	   (1055) an enlarged panel may open as its explainer. */
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
}

.card-layer-backdrop {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
}

.card-layer-shell {
	position: relative;
	display: flex;
	flex-direction: column;
	width: min(1180px, 94vw);
	max-height: 92vh;
	overflow: auto;
	background: #fff;
	border-radius: 6px;
	box-shadow: 0 12px 48px rgba(0, 0, 0, 0.28);
}

.card-layer-bar {
	position: sticky;
	top: 0;
	z-index: 1;
	display: flex;
	justify-content: flex-end;
	flex: 0 0 auto;
	padding: 4px 8px;
	background: #fff;
	border-bottom: 1px solid #f1f3f5;
}

.card-layer-close {
	border: 0;
	background: transparent;
	padding: 0 2px;
	line-height: 1;
	/* The card behind it is read at full size, so the one control that closes
	   it is not left at rail size: a 0.85rem X was a speck in the corner of a
	   1130px sheet. The glyph is the button's only content, so its size is the
	   button's size. */
	font-size: 1.05rem;
	color: #6c757d;
	cursor: pointer;
}

.card-layer-close:hover {
	color: #212529;
}

.card-host {
	width: 100%;
	box-sizing: border-box;
	/* A little inset so the enlarged card's own border does not read as the
	   shell's edge. */
	padding: 10px 12px 12px;
}

/* The enlarged card re-declares its sizes instead of being scaled: every chart
   here is chart.js with `maintainAspectRatio: false`, drawn to the box its
   container gives it, so a `transform` would only stretch a bitmap that is
   still laid out at pane size. Taller boxes give the chart a genuinely larger
   canvas, and the caption type grows with them so the card reads as a full-size
   version of itself rather than a magnified screenshot.
   The `#analysis-card-host` id is part of each selector because the card's own
   scoped rules (`.panel-chart` plus its data attribute) are just as specific;
   the id outranks them, which is what makes the population panels' fixed
   190/180/126px heights give way. */
#analysis-card-host :deep(.panel-chart) {
	height: 380px;
	min-height: 380px;
	/* In the rail the chart takes the leftover panel height; here the card is
	   exactly as tall as its content, so the box states its own height. */
	flex: 0 0 auto;
}

#analysis-card-host :deep(.species-chart) {
	height: 240px;
}

/* The pane's small-multiples grid: the cells grow with the chart inside. */
#analysis-card-host :deep(.species-cell) {
	min-height: 260px;
}

/* The card's type, re-declared for the box it is read in. Every size below is a
   rail size — the rail's cards are about 420px wide and their text runs 0.6rem
   to 0.85rem — and the same size in a ~1130px sheet reads as small print. Each
   one is lifted by about a third, the ratio the two boxes differ by, and floored
   at 0.85rem so no line of words stays at rail size. The order of the rail is
   kept rather than flattened: the figure stays loudest, then the panel's title,
   then the prose, then the labels and rows, then the keys' details. Nothing
   gains or loses weight here — a caption that was not bold in the rail is not
   bold at full size — and nothing that carried no words (the expander and info
   icons, the swatches, the bars) is touched: they are controls, not reading, and
   an icon that grew with the prose would shout. */

/* Headlines: a report panel's title and a population panel's, which sit at the
   same rank on the card. */
#analysis-card-host :deep(.report-panel-title),
#analysis-card-host :deep(.panel-title) {
	font-size: 1.05rem;
}

/* The population header's own title, one rank under a panel's in the rail and
   left one rank under it here. */
#analysis-card-host :deep(.header-title) {
	font-size: 1rem;
}

/* What a figure is, on one line: the caption over a chart and the name of a
   species cell. */
#analysis-card-host :deep(.chart-caption),
#analysis-card-host :deep(.species-name) {
	font-size: 0.95rem;
}

/* An empty panel is read, not glanced at — it says why there is nothing — so it
   takes prose size rather than the caption size the rail gives it. */
#analysis-card-host :deep(.panel-empty) {
	font-size: 0.95rem;
}

/* The prose: what a panel measures, the method's limits, the headline figure's
   own sentence. This is the type the operator actually reads, so it takes the
   largest lift, and the nesting that matters is kept — the method's list and
   its inline code span are in `em`, so they climb with their list. */
#analysis-card-host :deep(.panel-help),
#analysis-card-host :deep(.headline-quantity),
#analysis-card-host :deep(.method-list),
#analysis-card-host :deep(.note) {
	font-size: 0.9rem;
}

/* Keys: the band and outcome keys, read while decoding the figure beside them,
   so they stay a step under the prose that explains it. */
#analysis-card-host :deep(.legend),
#analysis-card-host :deep(.outcome-key),
#analysis-card-host :deep(.segment) {
	font-size: 0.9rem;
}

/* The quietest words left on the card: the filter hint above a table, a
   "capped" mark, a share figure, a species' slope badge. They were 0.6rem to
   0.68rem in the rail, so they take the floor. */
#analysis-card-host :deep(.filter-hint),
#analysis-card-host :deep(.cap-mark),
#analysis-card-host :deep(.share-value),
#analysis-card-host :deep(.badge) {
	font-size: 0.85rem;
}

/* A control that carries words rather than a glyph: the observer filter chip.
   It is boxed, so it takes a size that sits with the table it acts on instead
   of under it; the chip's icon is the one icon raised here, and only because it
   is set beside its own label. */
#analysis-card-host :deep(.filter-chip) {
	font-size: 0.9rem;
}

#analysis-card-host :deep(.filter-chip .bi) {
	font-size: 0.85rem;
}

/* Rows: the report tables, the three bespoke ones, and the site list whose label
   is a link and whose count sits beside its bar. One size, because they are read
   the same way — a figure in a row of figures. */
#analysis-card-host :deep(.report-table),
#analysis-card-host :deep(.observer-table),
#analysis-card-host :deep(.outcome-table),
#analysis-card-host :deep(.retreat-table),
#analysis-card-host :deep(.link),
#analysis-card-host :deep(.site-visits) {
	font-size: 0.9rem;
}

/* Column headers stay one step quieter than their rows, which is the relation
   the rail sets. The report tables set no header size of their own, so they
   inherit their rows and are left to do exactly that. */
#analysis-card-host :deep(.observer-table th),
#analysis-card-host :deep(.outcome-table th),
#analysis-card-host :deep(.retreat-table th) {
	font-size: 0.85rem;
}

/* A figure is the loudest thing on its card and stays the loudest: the figure
   and its two lines lift together, so the gap that makes the number read first
   is the gap it had. */
#analysis-card-host :deep(.stat-value) {
	font-size: 1.7rem;
}

#analysis-card-host :deep(.stat-label) {
	font-size: 0.9rem;
}

#analysis-card-host :deep(.stat-sub) {
	font-size: 0.85rem;
}

/* One box in the card is fixed in pixels for the rail's type, and a px column is
   really a character budget: hold the site list's name column still while its
   label grows and the row truncates a name the rail showed in full — showing
   *less* at full size than in the rail. The column takes the width its label
   needs at the new size; the bar track stays fluid and the count keeps its own
   width. Every other column in the card is a percentage and grows on its own. */
#analysis-card-host :deep(.site-row) {
	grid-template-columns: 152px minmax(0, 1fr) auto;
}
</style>
